import { AxiosInstance } from 'axios';
import fileDownload from 'js-file-download';
import { Customer } from '../interfaces/Customer';
import { NipInfo } from '../interfaces/NipInfo';
import { Provider } from '../interfaces/Provider';
import { getToken } from '../services/tokenservice';

type QualifiedSubmitPayload = {
  nipsToQualify: string[];
  nipsToUnQualify: string[];
};

export const createApiService = (axios: AxiosInstance) => ({
  addCustomer: (customer: Customer) => {
    return axios.post('/api/customers', { "customer": customer }, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
    });
  },

  fetchCustomers: async () => {
    const response = await axios.get<{ customers: Customer[] }>('/api/customers', {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (response.status !== 200) {
      throw new Error('Failed to fetch customers');
    }

    return response.data.customers;
  },

  fetchCustomer: async (nip: string) => {
    const response = await axios.get<{ customer: Customer }>('/api/customers/' + nip, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (response.status !== 200) {
      throw new Error('Failed to fetch customers');
    }

    return response.data.customer;
  },

  login: async (username: string, password: string) => {
    const response = await axios.post<{ access_token: string, token_type: string }>('/api/token', new URLSearchParams({
      username: username,
      password: password,
    }));

    if (response.status !== 200) {
      throw new Error('Login failed');
    }

    const { access_token, token_type } = response.data;
    localStorage.setItem('token', access_token);
  },

  async downloadDocuments(customers: Customer[], provider: Provider) {

    const response = await axios.post<Blob>(`/api/documents/fill-documents`, { "customers": customers, "provider": provider }, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      responseType: 'blob', // Important for downloading files
    });

    if (response.status !== 200) {
      throw new Error(`Failed to fetch documents: ${response.statusText}`);
    }

    fileDownload(response.data, `${provider}.zip`);
  },

  downloadCard: async (customer: Customer) => {
    const response = await axios.get(`/api/documents/generate-card/${customer.nip}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      responseType: 'blob',
    });

    if (response.status !== 200) {
      throw new Error(`Failed to fetch card: ${response.statusText}`);
    }

    fileDownload(response.data, `${customer.name}_${customer.surname}_SWO.pdf`);
  },

  async updateQualifiedCustomers(payload: QualifiedSubmitPayload) {
    const response = await axios.post(
      '/api/customers/set-customers-qualified',
      payload,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status !== 200) {
      throw new Error(`Failed to update qualified customers: ${response.statusText}`);
    }
  },

  async getNipInfo(nip: string) {
    const response = await axios.get<{ firm_info: NipInfo }>(
      `/api/customers/get-firm-info/${nip}`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (response.status !== 200) {
      throw new Error(`Failed to fetch NIP info: ${response.statusText}`);
    }
    return response.data.firm_info;
  },

  getParpSheetNames: async () => {
    const response = await axios.get<string[]>(`/api/documents/get-parp-sheet-names`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.status !== 200) {
      throw new Error(`Failed to fetch PARP sheet names: ${response.statusText}`);
    }

    return response.data;
  },

  downloadParpDocs: async (firstIdCount: number, date: string, sheet: string) => {
    const apiResponse = await axios.get<{ url: string }>(`/api/documents/generate-parp-docs?firstIdCount=${firstIdCount}&date=${date}&sheet=${encodeURIComponent(sheet)}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      }
    });

    if (apiResponse.status !== 200) {
      throw new Error(`Failed to fetch PARP docs: ${apiResponse.statusText}`);
    }

    const storageResponse = await axios.get(apiResponse.data.url, {
      responseType: 'blob',
    });

    fileDownload(storageResponse.data, 'parp_docs.zip');
  },

  getLastParpDocsId: async () => {
    const response = await axios.get<number>(`/api/documents/get-last-parp-docs-id`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.status !== 200) {
      throw new Error(`Failed to fetch next PARP docs ID: ${response.statusText}`);
    }

    return response.data;
  },
})